/* eslint-disable no-underscore-dangle */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  inject,
} from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { FormControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { SelectionModel } from '@angular/cdk/collections';
import { IdName } from '../../models/id-name';

@Component({
  selector: 'app-ui-table-selection',
  templateUrl: './ui-table-selection.component.html',
  styleUrls: ['./ui-table-selection.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class UiTableSelectionComponent<T extends IdName> implements OnInit, OnChanges, OnDestroy {
  @Input() set displayedColumns(columns: string[]) {
    this._displayedColumns = [this.selectColName, ...columns];
  }
  @Input() set dataSource(data: T[] | (undefined | null)) {
    if (data) {
      this._dataSource = new MatTableDataSource<T>(data);
      this.applyControlValueToSelection();
    }
  }
  @Input() set control(control: FormControl<string[]>) {
    this._control = control;
    this.applyControlValueToSelection();
  }
  @Input() toggleAllEnabled = true;
  @Input() disabled = false;
  selection = new SelectionModel<T>(true, [], true, (a, b) => a?.id === b?.id);
  _dataSource: MatTableDataSource<T>;
  _displayedColumns: string[];
  _control: FormControl<string[]>;
  selectColName = 'select';
  errorsColName = 'errors';
  headerChecked = false;
  headerIndeterminate = false;
  private controlValuesInitialized = false;
  private syncingFromControl = false;
  private subscription = new Subscription();
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  ngOnInit() {
    this.subscription.add(this.selection.changed.subscribe(() => this.onSelectionChanged()));
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.dataSource || changes.control) {
      this.applyControlValueToSelection();
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }

    this.selection.select(...this._dataSource.data);
  }

  toggleSelection(row: T) {
    if (!this.disabled) {
      this.selection.toggle(row);
    }
  }

  private onSelectionChanged(): void {
    this.updateHeaderCheckboxState();
    if (this.syncingFromControl || !this._control) {
      return;
    }
    this._control.setValue(this.selection.selected.map((selected: T) => selected.id));
    this._control.updateValueAndValidity();
    if (this.controlValuesInitialized) {
      this._control.markAsDirty();
    }
    this.controlValuesInitialized = true;
  }

  private applyControlValueToSelection(): void {
    if (!this._control || !this._dataSource) {
      return;
    }
    const selectedIds = this._control.value ?? [];
    const items = this._dataSource.data.filter((item) => selectedIds.includes(item.id));
    this.syncingFromControl = true;
    if (items.length) {
      this.selection.setSelection(...items);
    } else {
      this.selection.clear();
    }
    this.syncingFromControl = false;
    this.controlValuesInitialized = true;
    this.updateHeaderCheckboxState();
    this.changeDetectorRef.markForCheck();
  }

  private updateHeaderCheckboxState() {
    const hasValue = this.selection.hasValue();
    const numSelected = this.selection.selected.length;
    const numRows = this._dataSource?.data?.length ?? 0;
    const allSelected = numRows > 0 && numSelected === numRows;
    this.headerChecked = hasValue && allSelected;
    this.headerIndeterminate = hasValue && !allSelected;
  }

  private isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this._dataSource?.data?.length ?? 0;
    return numSelected === numRows;
  }
}
